import React, { useEffect, useRef, useState } from "react";
import usePrivateLink from "../../config/usePrivateLink";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import AcademicHeader from "../../Academic/AcademicHeader";

import {
  addBanner,
  Bannerlist,
  BannerDelete,
  BannerStatus,
} from "../../../api/banner";

import { ToastMain, SweetAlertShow, LoadingDiv } from "../../master/Master";
import { imgBaseUrl } from "../../../api/api";
import Swal from "sweetalert2";

const BANNER_WIDTH = 1200;
const BANNER_HEIGHT = 400;
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const getImageUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;

  return `${String(imgBaseUrl || "").replace(/\/+$/, "")}/${String(
    path,
  ).replace(/^\/+/, "")}`;
};

export default function Banner() {
  const hasAccess = usePrivateLink();

  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [viewBanner, setViewBanner] = useState(null);

  const formRef = useRef(null);
  const modalRef = useRef(null);
  const fileInputRef = useRef(null);
  const submittingRef = useRef(false);

  const canAddBanner = hasAccess("/settings/banner/add");
  const canUpdateStatus = hasAccess("/settings/banner/status");
  const canDeleteBanner = hasAccess("/settings/banner/delete");

  useEffect(() => {
    let mounted = true;

    const fetchBanners = async () => {
      setLoading(true);

      try {
        const res = await Bannerlist();

        if (mounted) {
          setBanners(Array.isArray(res.data?.data) ? res.data.data : []);
        }
      } catch (err) {
        console.error("Error fetching banners:", err);

        if (mounted) {
          SweetAlertShow(
            "error",
            "Unable to load banners",
            err?.response?.data?.message || "Please try again.",
          );
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchBanners();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const resetForm = () => {
    setError({});
    setSelectedFile(null);
    setDescription("");
    setIsActive(true);

    if (previewUrl) URL.revokeObjectURL(previewUrl);

    setPreviewUrl("");

    if (formRef.current) formRef.current.reset();
  };

  useEffect(() => {
    const modalEl = modalRef.current;
    if (!modalEl) return;

    const handleHidden = () => {
      resetForm();
    };

    modalEl.addEventListener("hidden.bs.modal", handleHidden);

    return () => {
      modalEl.removeEventListener("hidden.bs.modal", handleHidden);
    };
  }, [previewUrl]);

  useEffect(() => {
    if (!viewBanner) return;

    const modalElement = document.getElementById("viewBannerModal");
    const Modal = window.bootstrap?.Modal;

    if (!modalElement || !Modal) return;

    const modal = Modal.getOrCreateInstance(modalElement);
    modal.show();

    return () => {
      modal.hide();
    };
  }, [viewBanner]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];

    setError((prev) => ({
      ...prev,
      banner: undefined,
      bannerImage: undefined,
    }));

    if (!file) {
      setSelectedFile(null);
      setPreviewUrl("");
      return;
    }

    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError((prev) => ({
        ...prev,
        banner: "Please select a JPG, PNG or WebP image.",
      }));
      e.target.value = "";
      setSelectedFile(null);
      setPreviewUrl("");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError((prev) => ({
        ...prev,
        banner:
          "The selected image exceeds 5 MB. Please choose a smaller image.",
      }));
      e.target.value = "";
      setSelectedFile(null);
      setPreviewUrl("");
      return;
    }

    const nextUrl = URL.createObjectURL(file);

    setPreviewUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return nextUrl;
    });

    setSelectedFile(file);
  };

  const handleAddBanner = async (e) => {
    e.preventDefault();

    if (submittingRef.current) return;

    if (!selectedFile) {
      setError({ banner: "Please select a banner image." });
      return;
    }

    if (!description.trim()) {
      setError({ description: "Please enter a description." });
      return;
    }

    submittingRef.current = true;
    setLoading(true);
    setError({});

    const formData = new FormData();

    // Keep the existing backend field names.
    formData.append("banner", selectedFile);
    formData.append("description", description.trim());
    formData.append("status", isActive ? 1 : 0);

    try {
      const res = await addBanner(formData);

      if (res.data?.status) {
        if (res.data.data) {
          setBanners((prev) => [...prev, res.data.data]);
        }

        const modal = window.bootstrap?.Modal?.getInstance(modalRef.current);

        if (modal) modal.hide();

        resetForm();

        ToastMain.fire({
          icon: "success",
          title: "Banner added successfully",
        });
      } else {
        const errors = res.data?.errors || {};

        setError({
          ...errors,
          banner:
            errors.banner?.[0] || errors.bannerImage?.[0] || errors.banner,
          description: errors.description?.[0] || errors.description,
        });

        ToastMain.fire({
          icon: "error",
          title: res.data?.message || "Unable to add banner",
        });
      }
    } catch (err) {
      console.error("Error adding banner:", err);

      const errors = err?.response?.data?.errors || {};

      setError({
        ...errors,
        banner: errors.banner?.[0] || errors.bannerImage?.[0] || errors.banner,
        description: errors.description?.[0] || errors.description,
      });

      SweetAlertShow(
        "error",
        "Unable to add banner",
        err?.response?.data?.message || err?.message || "Please try again.",
      );
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  const handleDeleteBanner = async (id) => {
    const result = await Swal.fire({
      title: "Delete banner?",
      text: "This banner will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Keep banner",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    setLoading(true);

    try {
      const res = await BannerDelete(id);

      if (res.data?.status) {
        setBanners((prev) => prev.filter((banner) => banner.id !== id));

        ToastMain.fire({
          icon: "success",
          title: "Banner deleted successfully",
        });
      } else {
        ToastMain.fire({
          icon: "error",
          title: res.data?.message || "Unable to delete banner",
        });
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleStatus = async (id, currentStatus) => {
    const nextStatus = Number(currentStatus) === 1 ? 0 : 1;

    setLoading(true);

    try {
      const res = await BannerStatus({
        id,
        status: nextStatus,
      });

      if (res.data?.status) {
        setBanners((prev) =>
          prev.map((banner) =>
            banner.id === id ? { ...banner, status: nextStatus } : banner,
          ),
        );

        ToastMain.fire({
          icon: "success",
          title: nextStatus
            ? "Banner activated successfully"
            : "Banner deactivated successfully",
        });
      } else {
        ToastMain.fire({
          icon: "error",
          title: res.data?.message || "Unable to update status",
        });
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || "Unable to update status",
      );
    } finally {
      setLoading(false);
    }
  };

  const filteredBanners = banners.filter((banner) => {
    const matchesSearch = [banner.description, banner.banner_img]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(search.trim().toLowerCase());

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && Number(banner.status) === 1) ||
      (statusFilter === "inactive" && Number(banner.status) !== 1);

    return matchesSearch && matchesStatus;
  });

  const activeCount = banners.filter(
    (banner) => Number(banner.status) === 1,
  ).length;

  const openModal = () => {
    resetForm();
  };

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="container-fluid pb-4">
              <div className="row mt-4">
                <AcademicHeader />

                <div className="col-12">
                  {/* Page heading */}
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <div className="banner-heading-icon">
                          <i className="ri-image-2-line fs-3" />
                        </div>
                        <h4 className="mb-0 fw-bold">Banner Management</h4>
                      </div>
                      <p className="text-muted mb-0">
                        Manage the promotional banners displayed in your mobile
                        application.
                      </p>
                    </div>

                    {canAddBanner && (
                      <button
                        type="button"
                        className="btn btn-primary d-flex align-items-center gap-2 px-3 py-2"
                        data-bs-toggle="modal"
                        data-bs-target="#addBannerModal"
                        onClick={openModal}
                      >
                        <i className="ri-add-line fs-5" />
                        Add new banner
                      </button>
                    )}
                  </div>

                  {/* Summary cards */}
                  {/* <div className="row g-3 mb-4">
                    <div className="col-12 col-sm-6 col-xl-4">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body d-flex align-items-center gap-3">
                          <div className="banner-stat-icon banner-stat-blue">
                            <i className="ri-gallery-line fs-3" />
                          </div>
                          <div>
                            <div className="text-muted small">
                              Total banners
                            </div>
                            <h4 className="mb-0 fw-bold">{banners.length}</h4>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-xl-4">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body d-flex align-items-center gap-3">
                          <div className="banner-stat-icon banner-stat-green">
                            <i className="ri-checkbox-circle-line fs-3" />
                          </div>
                          <div>
                            <div className="text-muted small">
                              Active banners
                            </div>
                            <h4 className="mb-0 fw-bold">{activeCount}</h4>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-xl-4">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body d-flex align-items-center gap-3">
                          <div className="banner-stat-icon banner-stat-purple">
                            <i className="ri-smartphone-line fs-3" />
                          </div>
                          <div>
                            <div className="text-muted small">
                              Target platform
                            </div>
                            <h6 className="mb-0 fw-bold">
                              Student & Staff App
                            </h6>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div> */}

                  {/* Banner list */}
                  <div className="card border-0 shadow-sm overflow-hidden">
                    <div className="card-header bg-transparent border-bottom py-3">
                      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <div>
                          <h5 className="mb-1 fw-bold">Uploaded banners</h5>
                          <p className="text-muted small mb-0">
                            Preview, activate or remove your banners.
                          </p>
                        </div>

                        <div className="d-flex flex-wrap gap-2">
                          <div className="input-group banner-search">
                            <span className="input-group-text bg-transparent">
                              <i className="ri-search-line" />
                            </span>
                            <input
                              type="search"
                              className="form-control"
                              placeholder="Search banners..."
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                              aria-label="Search banners"
                            />
                          </div>

                          <select
                            className="form-select banner-status-filter"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            aria-label="Filter by banner status"
                          >
                            <option value="all">All statuses</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                          <thead>
                            <tr>
                              <th className="text-center" style={{ width: 65 }}>
                                #
                              </th>
                              <th style={{ minWidth: 220 }}>Banner preview</th>
                              <th style={{ minWidth: 220 }}>Description</th>
                              <th
                                className="text-center"
                                style={{ width: 150 }}
                              >
                                Status
                              </th>
                              {(canUpdateStatus || canDeleteBanner) && (
                                <th
                                  className="text-center"
                                  style={{ width: 130 }}
                                >
                                  Actions
                                </th>
                              )}
                            </tr>
                          </thead>

                          <tbody>
                            {filteredBanners.length > 0 ? (
                              filteredBanners.map((banner, index) => (
                                <tr key={banner.id || index}>
                                  <td className="text-center text-muted fw-semibold">
                                    {String(index + 1).padStart(2, "0")}
                                  </td>

                                  <td>
                                    <button
                                      type="button"
                                      className="banner-table-preview border-0 p-0"
                                      onClick={() => setViewBanner(banner)}
                                      title="Click to view full banner"
                                      aria-label={`View banner: ${banner.description || "Banner"}`}
                                    >
                                      <img
                                        src={getImageUrl(banner.banner_img)}
                                        alt={
                                          banner.description ||
                                          "Application banner"
                                        }
                                        loading="lazy"
                                        onError={(e) => {
                                          e.currentTarget.style.visibility =
                                            "hidden";
                                        }}
                                      />

                                      <span className="banner-preview-overlay">
                                        <i className="ri-zoom-in-line" />
                                        <span>View</span>
                                      </span>
                                    </button>
                                  </td>

                                  <td>
                                    <div className="fw-semibold mb-1">
                                      {banner.description || "Untitled banner"}
                                    </div>
                                  </td>

                                  <td className="text-center">
                                    <span
                                      className={`badge rounded-pill px-3 py-2 ${
                                        Number(banner.status) === 1
                                          ? "bg-label-success"
                                          : "bg-label-secondary"
                                      }`}
                                    >
                                      <i
                                        className={`me-1 ${
                                          Number(banner.status) === 1
                                            ? "ri-checkbox-circle-line"
                                            : "ri-pause-circle-line"
                                        }`}
                                      />
                                      {Number(banner.status) === 1
                                        ? "Active"
                                        : "Inactive"}
                                    </span>
                                  </td>

                                  {(canUpdateStatus || canDeleteBanner) && (
                                    <td className="text-center">
                                      <div className="d-inline-flex align-items-center gap-2">
                                        {canUpdateStatus && (
                                          <button
                                            type="button"
                                            className={`btn btn-sm ${
                                              Number(banner.status) === 1
                                                ? "btn-outline-warning"
                                                : "btn-outline-success"
                                            }`}
                                            onClick={() =>
                                              handleStatus(
                                                banner.id,
                                                banner.status,
                                              )
                                            }
                                            title={
                                              Number(banner.status) === 1
                                                ? "Deactivate banner"
                                                : "Activate banner"
                                            }
                                            aria-label={
                                              Number(banner.status) === 1
                                                ? "Deactivate banner"
                                                : "Activate banner"
                                            }
                                          >
                                            <i
                                              className={`${
                                                Number(banner.status) === 1
                                                  ? "ri-eye-off-line"
                                                  : "ri-eye-line"
                                              } fs-5`}
                                            />
                                          </button>
                                        )}

                                        {canDeleteBanner && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() =>
                                              handleDeleteBanner(banner.id)
                                            }
                                            title="Delete banner"
                                            aria-label="Delete banner"
                                          >
                                            <i className="ri-delete-bin-6-line fs-5" />
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  )}
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan={
                                    canUpdateStatus || canDeleteBanner ? 5 : 4
                                  }
                                >
                                  <div className="text-center py-5 px-3">
                                    <div className="banner-empty-icon mb-3">
                                      <i className="ri-image-add-line fs-1" />
                                    </div>
                                    <h6 className="fw-bold mb-2">
                                      {banners.length
                                        ? "No matching banners"
                                        : "No banners uploaded yet"}
                                    </h6>
                                    <p className="text-muted mb-3">
                                      {banners.length
                                        ? "Try a different search or status filter."
                                        : "Upload your first banner to display it in the mobile application."}
                                    </p>
                                    {!banners.length && canAddBanner && (
                                      <button
                                        type="button"
                                        className="btn btn-primary"
                                        data-bs-toggle="modal"
                                        data-bs-target="#addBannerModal"
                                        onClick={openModal}
                                      >
                                        <i className="ri-add-line me-1" />
                                        Add your first banner
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="card-footer bg-transparent border-top py-3">
                      <small className="text-muted">
                        Showing {filteredBanners.length} of {banners.length}{" "}
                        banners
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Add banner modal */}
            <div
              ref={modalRef}
              className="modal fade"
              id="addBannerModal"
              tabIndex="-1"
              aria-labelledby="addBannerModalLabel"
              aria-hidden="true"
            >
              <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content border-0 shadow-lg">
                  <div className="modal-header px-4 py-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="banner-heading-icon">
                        <i className="ri-image-add-line fs-3" />
                      </div>
                      <div>
                        <h5
                          className="modal-title fw-bold mb-1"
                          id="addBannerModalLabel"
                        >
                          Add new banner
                        </h5>
                        <p className="text-muted small mb-0">
                          Upload an image for your mobile app.
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="btn-close"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                      disabled={loading}
                    />
                  </div>

                  <form ref={formRef} onSubmit={handleAddBanner}>
                    <div className="modal-body p-4">
                      <div className="row g-4">
                        <div className="col-12">
                          {/* Upload instructions */}
                          <div className="banner-guidelines">
                            <div className="d-flex align-items-start gap-3">
                              <div className="banner-guideline-icon">
                                <i className="ri-information-line fs-4" />
                              </div>
                              <div className="flex-grow-1">
                                <h6 className="fw-bold mb-2">
                                  Recommended banner specifications
                                </h6>

                                <div className="row g-3">
                                  <div className="col-6 col-md-3">
                                    <div className="banner-spec">
                                      <i className="ri-aspect-ratio-line" />
                                      <span>Image size</span>
                                      <strong>1200 × 400 px</strong>
                                    </div>
                                  </div>
                                  <div className="col-6 col-md-3">
                                    <div className="banner-spec">
                                      <i className="ri-crop-line" />
                                      <span>Aspect ratio</span>
                                      <strong>3:1 (wide)</strong>
                                    </div>
                                  </div>
                                  <div className="col-6 col-md-3">
                                    <div className="banner-spec">
                                      <i className="ri-file-image-line" />
                                      <span>File format</span>
                                      <strong>JPG, PNG, WebP</strong>
                                    </div>
                                  </div>
                                  <div className="col-6 col-md-3">
                                    <div className="banner-spec">
                                      <i className="ri-hard-drive-2-line" />
                                      <span>Maximum size</span>
                                      <strong>5 MB</strong>
                                    </div>
                                  </div>
                                </div>

                                <div className="small text-muted mt-3">
                                  <i className="ri-lightbulb-line me-1" />
                                  Use a wide landscape image. Keep important
                                  text and logos away from the edges. The app
                                  may crop images to fit different screens.
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Image upload and preview */}
                        <div className="col-12">
                          <label
                            htmlFor="bannerImage"
                            className="form-label fw-semibold"
                          >
                            Banner image <span className="text-danger">*</span>
                          </label>

                          <input
                            ref={fileInputRef}
                            type="file"
                            className={`form-control ${
                              error.banner || error.bannerImage
                                ? "is-invalid"
                                : ""
                            }`}
                            id="bannerImage"
                            name="bannerImage"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={handleFileChange}
                            required
                          />

                          {(error.banner || error.bannerImage) && (
                            <div className="text-danger small mt-2">
                              {Array.isArray(error.banner || error.bannerImage)
                                ? (error.banner || error.bannerImage)[0]
                                : error.banner || error.bannerImage}
                            </div>
                          )}

                          <div className="banner-live-preview mt-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span className="small fw-semibold">
                                Live preview
                              </span>
                              <span className="badge bg-label-primary">
                                Mobile banner · 3:1
                              </span>
                            </div>

                            {previewUrl ? (
                              <img
                                src={previewUrl}
                                alt="Selected banner preview"
                                className="banner-selected-image"
                              />
                            ) : (
                              <div className="banner-preview-placeholder">
                                <i className="ri-image-add-line fs-1 mb-2" />
                                <strong>
                                  Your banner preview will appear here
                                </strong>
                                <span className="small">
                                  Recommended dimensions: 1200 × 400 pixels
                                </span>
                              </div>
                            )}
                          </div>

                          {selectedFile && (
                            <div className="d-flex align-items-center gap-2 mt-2 small text-muted">
                              <i className="ri-file-check-line text-success fs-5" />
                              <span className="text-break">
                                {selectedFile.name} ·{" "}
                                {(selectedFile.size / (1024 * 1024)).toFixed(2)}{" "}
                                MB
                              </span>
                              <button
                                type="button"
                                className="btn btn-sm btn-link text-danger ms-auto"
                                onClick={() => {
                                  setSelectedFile(null);
                                  setPreviewUrl("");
                                  if (fileInputRef.current) {
                                    fileInputRef.current.value = "";
                                  }
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Description */}
                        <div className="col-12">
                          <label
                            htmlFor="bannerDesc"
                            className="form-label fw-semibold"
                          >
                            Banner description{" "}
                            <span className="text-danger">*</span>
                          </label>

                          <input
                            type="text"
                            className={`form-control ${
                              error.description ? "is-invalid" : ""
                            }`}
                            id="bannerDesc"
                            name="bannerDesc"
                            placeholder="e.g. Welcome to the new academic session"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            maxLength={255}
                            required
                          />

                          {error.description && (
                            <div className="invalid-feedback">
                              {Array.isArray(error.description)
                                ? error.description[0]
                                : error.description}
                            </div>
                          )}

                          <div className="form-text">
                            Enter a short, meaningful description to identify
                            this banner.
                          </div>
                        </div>

                        {/* Status */}
                        <div className="col-12">
                          <div className="banner-status-box d-flex align-items-center justify-content-between gap-3">
                            <div className="d-flex align-items-center gap-3">
                              <div
                                className={`banner-status-icon ${
                                  isActive
                                    ? "banner-status-enabled"
                                    : "banner-status-disabled"
                                }`}
                              >
                                <i
                                  className={`${
                                    isActive ? "ri-eye-line" : "ri-eye-off-line"
                                  } fs-5`}
                                />
                              </div>
                              <div>
                                <div className="fw-semibold">
                                  Display banner immediately
                                </div>
                                <small className="text-muted">
                                  {isActive
                                    ? "This banner will be active after saving."
                                    : "This banner will be saved as inactive."}
                                </small>
                              </div>
                            </div>

                            <div className="form-check form-switch mb-0">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="bannerStatus"
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="modal-footer px-4 py-3">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        data-bs-dismiss="modal"
                        disabled={loading}
                      >
                        Cancel
                      </button>

                      <button
                        type="submit"
                        className="btn btn-primary px-4"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving banner...
                          </>
                        ) : (
                          <>
                            <i className="ri-save-line me-1" />
                            Save banner
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* Full Banner Preview Modal */}
            <div
              className="modal fade"
              id="viewBannerModal"
              tabIndex="-1"
              aria-labelledby="viewBannerModalLabel"
              aria-hidden="true"
            >
              <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content border-0 shadow-lg">
                  <div className="modal-header">
                    <div>
                      <h5
                        className="modal-title fw-bold"
                        id="viewBannerModalLabel"
                      >
                        Banner Preview
                      </h5>

                      <small className="text-muted">
                        {viewBanner?.description || "Application banner"}
                      </small>
                    </div>

                    <button
                      type="button"
                      className="btn-close"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                    />
                  </div>

                  <div className="modal-body p-3 p-md-4">
                    {viewBanner && (
                      <>
                        <div className="banner-full-preview">
                          <img
                            src={getImageUrl(viewBanner.banner_img)}
                            alt={
                              viewBanner.description || "Full banner preview"
                            }
                          />
                        </div>
                      </>
                    )}
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      data-bs-dismiss="modal"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Component-scoped styles; no external stylesheet required */}
      <style>{`
        .banner-heading-icon {
          width: 44px;
          height: 44px;
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 13px;
          color: var(--bs-primary);
          background: rgba(var(--bs-primary-rgb), .10);
        }

        .banner-stat-icon {
          width: 52px;
          height: 52px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 15px;
        }

        .banner-stat-blue {
          color: var(--bs-primary);
          background: rgba(var(--bs-primary-rgb), .12);
        }

        .banner-stat-green {
          color: var(--bs-success);
          background: rgba(var(--bs-success-rgb), .12);
        }

        .banner-stat-purple {
          color: var(--bs-info);
          background: rgba(var(--bs-info-rgb), .12);
        }

        .banner-search {
          width: min(100%, 250px);
        }

        .banner-status-filter {
          width: 145px;
        }

        .banner-table-preview {
          width: 160px;
          aspect-ratio: 3 / 1;
          position: relative;
          overflow: hidden;
          border: 1px solid var(--bs-border-color);
          border-radius: 10px;
          background: var(--bs-tertiary-bg);
        }

        .banner-table-preview img {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
        }

        .banner-preview-ratio {
          position: absolute;
          right: 5px;
          bottom: 5px;
          padding: 2px 6px;
          font-size: 10px;
          border-radius: 5px;
          color: white;
          background: rgba(0, 0, 0, .6);
        }

        .banner-empty-icon {
          width: 76px;
          height: 76px;
          margin-inline: auto;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 22px;
          color: var(--bs-primary);
          background: rgba(var(--bs-primary-rgb), .09);
        }

        .banner-guidelines {
          padding: 18px;
          border: 1px solid var(--bs-border-color);
          border-radius: 14px;
          background: var(--bs-tertiary-bg);
        }

        .banner-guideline-icon {
          width: 38px;
          height: 38px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--bs-primary);
          background: rgba(var(--bs-primary-rgb), .12);
          border-radius: 10px;
        }

        .banner-spec {
          height: 100%;
          min-height: 84px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 4px;
          padding: 10px;
          border-radius: 10px;
          border: 1px solid var(--bs-border-color);
          background: var(--bs-body-bg);
        }

        .banner-spec i {
          font-size: 17px;
          color: var(--bs-primary);
        }

        .banner-spec span {
          color: var(--bs-secondary-color);
          font-size: 11px;
        }

        .banner-spec strong {
          font-size: 12px;
          overflow-wrap: anywhere;
        }

        .banner-live-preview {
          padding: 12px;
          border: 1px solid var(--bs-border-color);
          border-radius: 12px;
          background: var(--bs-tertiary-bg);
        }

        .banner-selected-image {
          display: block;
          width: 100%;
          aspect-ratio: 3 / 1;
          object-fit: cover;
          border-radius: 9px;
          background: var(--bs-secondary-bg);
        }

        .banner-preview-placeholder {
          width: 100%;
          aspect-ratio: 3 / 1;
          min-height: 100px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 5px;
          padding: 12px;
          text-align: center;
          color: var(--bs-secondary-color);
          border: 1px dashed var(--bs-border-color);
          border-radius: 9px;
        }

        .banner-preview-placeholder i {
          color: var(--bs-primary);
        }

        .banner-preview-placeholder strong {
          color: var(--bs-body-color);
          font-size: 13px;
        }

        .banner-status-box {
          padding: 15px;
          border: 1px solid var(--bs-border-color);
          border-radius: 12px;
        }

        .banner-status-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          border-radius: 11px;
        }

        .banner-status-enabled {
          color: var(--bs-success);
          background: rgba(var(--bs-success-rgb), .12);
        }

        .banner-status-disabled {
          color: var(--bs-secondary-color);
          background: var(--bs-secondary-bg);
        }

        .banner-spec strong,
        .banner-status-box,
        .banner-guidelines {
          overflow-wrap: anywhere;
        }

        .banner-table-preview {
  display: block;
  position: relative;
  width: 160px;
  aspect-ratio: 3 / 1;
  overflow: hidden;
  border-radius: 10px;
  cursor: zoom-in;
  background: var(--bs-tertiary-bg);
}

.banner-table-preview img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.25s ease;
}

.banner-table-preview:hover img {
  transform: scale(1.05);
}

.banner-preview-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  background: rgba(0, 0, 0, 0.48);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.banner-table-preview:hover .banner-preview-overlay,
.banner-table-preview:focus-visible .banner-preview-overlay {
  opacity: 1;
}

.banner-preview-overlay i {
  font-size: 20px;
}

.banner-full-preview {
  width: 100%;
  overflow: hidden;
  border-radius: 12px;
  background: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
}

.banner-full-preview img {
  display: block;
  width: 100%;
  height: auto;
  max-height: 70vh;
  object-fit: contain;
  margin: 0 auto;
}

@media (max-width: 575.98px) {
  .banner-preview-overlay {
    opacity: 1;
  }

  .banner-table-preview {
    width: 130px;
  }
}

        @media (max-width: 575.98px) {
          .banner-search {
            width: 100%;
          }

          .banner-status-filter {
            width: 100%;
          }

          .banner-guidelines {
            padding: 12px;
          }

          .banner-table-preview {
            width: 130px;
          }

          .banner-preview-placeholder {
            aspect-ratio: 2.5 / 1;
          }
        }
      `}</style>
    </>
  );
}
